/**
 * Transport intents: play, skip, seek, volume, shuffle and repeat.
 *
 * Buttons, keyboard shortcuts and the deck's own 3D controls all funnel
 * through here, so a gesture and its shortcut can never drift apart.
 */

import * as player from '../state/player.ts';
import * as ui from '../state/ui.ts';
import type { RepeatMode, Track } from '../types/media.ts';
import { hardware } from './hardware.ts';
import { loadTrack, skip, startPlayback } from './playback.ts';

const REPEAT_CYCLE: Record<RepeatMode, RepeatMode> = { off: 'all', all: 'one', one: 'off' };
const REPEAT_LABEL: Record<RepeatMode, string> = {
  off: 'Repeat off',
  all: 'Repeat all',
  one: 'Repeat one',
};

/** Seconds into a record after which "previous" restarts it rather than skipping back. */
const RESTART_WINDOW = 4;

export async function toggleTransport(): Promise<void> {
  const { tt } = hardware();
  const { deck } = player;
  if (!deck.track) return;

  if (deck.playing) {
    deck.pause();
    tt.setSpinning(false);
    void deck.demo.sfxPowerDown().catch(() => undefined);
    void tt.setArmDown(false, 300);
  } else {
    await startPlayback();
  }
}

export const next = (): Promise<boolean> => skip(1);

export async function previous(): Promise<void> {
  if (player.deck.position > RESTART_WINDOW) {
    player.deck.seek(0);
    player.setPosition(0);
    return;
  }
  await skip(-1);
}

export function seekBy(delta: number): void {
  player.deck.seekBy(delta);
  player.setPosition(player.deck.position);
}

export function seekToFraction(f: number): void {
  player.deck.seekTo(f);
  player.setPosition(player.deck.position);
}

export const setVolume = (v: number): void => {
  player.deck.setVolume(v);
};

export function nudgeVolume(delta: number): void {
  player.deck.setMuted(false);
  player.deck.setVolume(player.deck.volume + delta);
  ui.toast(`Volume ${Math.round(player.deck.volume * 100)}%`, 900);
}

export const toggleMute = (): void => {
  player.deck.setMuted(!player.deck.muted);
};

export function toggleShuffle(): void {
  const on = !player.deck.playlist.shuffle;
  player.deck.playlist.shuffle = on;
  player.setShuffle(on);
  ui.toast(on ? 'Shuffle on' : 'Shuffle off', 1100);
}

export function cycleRepeat(): void {
  const mode = REPEAT_CYCLE[player.deck.playlist.repeat];
  player.deck.playlist.repeat = mode;
  player.setRepeat(mode);
  ui.toast(REPEAT_LABEL[mode], 1100);
}

/** Plays a track from a list, closing the sheet behind it. */
export function playFromList(track: Track, list: readonly Track[], index: number): void {
  ui.closeSheet();
  void loadTrack(track, list, index, { autoplay: true });
}
